import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { formatDate } from "src/Common/common";
import { handleError } from "src/Common/function";
import TicketService from "src/Services/API/ticket.service";
import CompanyManagementService from "src/Services/API/companymanagement.service";
import UserService from "src/Services/API/user.service";
import { setTicketNumber } from "src/Redux/Slices/HeaderSlice";
import useTicketListRealtime from "src/hooks/useTicketListRealtime";

const getStatusLabel = (ticket) => {
  if (typeof ticket?.status === "string" && ticket.status.trim()) {
    return ticket.status;
  }
  if (typeof ticket?.status === "number") {
    if (ticket.status === 100) return "Closed";
    if (ticket.status === 0) return "Open";
  }
  if (ticket?.isClosed) return "Closed";
  return "Open";
};

const TicketList = () => {
  const currentUser = UserService.getCurrentUser();
  const isSuperAdmin = currentUser?.roles?.[0]?.name === "Super Admin";
  const [tickets, setTickets] = useState([]);
  const [unreadIds, setUnreadIds] = useState(new Set());
  // Super Admins manage tickets across companies; "" means all companies.
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const companyNameByIdentifier = useMemo(() => {
    const map = {};
    companies.forEach((c) => {
      map[c.identifier] = c.name;
    });
    return map;
  }, [companies]);

  const loadTickets = async () => {
    try {
      const response = await TicketService.getAll(
        undefined,
        undefined,
        isSuperAdmin ? selectedCompany || undefined : undefined,
      );
      if (response.status == 200) {
        const rawTickets = Array.isArray(response.data)
          ? response.data
          : response.data?.tickets || [];

        setTickets(rawTickets);
      }
    } catch (error) {
      handleError(error);
    }
  };

  const loadUnreadIds = async () => {
    try {
      const res = await TicketService.getUnreadIds();
      setUnreadIds(new Set(res.data));
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    loadTickets();
    loadUnreadIds();
  }, [currentUser?.id, selectedCompany]);

  // Populate the company selector (Super Admin only).
  useEffect(() => {
    if (!isSuperAdmin) return;
    CompanyManagementService.list()
      .then((res) => setCompanies(res.data || []))
      .catch(() => {});
  }, [isSuperAdmin]);

  useTicketListRealtime({
    onRefresh: () => {
      loadTickets();
      loadUnreadIds();
    },
  });

  return (
    <div className="w-full px-5">
      {isSuperAdmin && (
        <div className="flex items-center gap-2 py-3">
          <span className="font-OpenSansSemiBold text-[13px] text-titleColor">Company:</span>
          <select
            value={selectedCompany}
            onChange={(e) => setSelectedCompany(e.target.value)}
            className="border-[1px] border-borderColor rounded-[6px] px-3 h-[32px] text-[13px] font-OpenSansMedium text-titleColor"
          >
            <option value="">All Companies</option>
            {companies.map((c) => (
              <option key={c.id ?? c.identifier} value={c.identifier}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      )}
      <table className="w-full">
        <thead>
          <tr className="border-t-[1px] border-b-[1px] border-borderColor">
            <th className="border-r-[1px] border-borderColor w-[110px]">
              <div className="font-OpenSansRegular font-[700] text-[13px] flex items-center justify-center h-[40px] text-titleColor text-center">
                Ticket #
              </div>
            </th>
            <th className="border-r-[1px] border-borderColor w-[90px]">
              <div className="font-OpenSansRegular font-[700] text-[13px] flex items-center justify-center h-[40px] text-titleColor text-center">
                WO #
              </div>
            </th>
            <th className="border-r-[1px] border-borderColor w-[90px]">
              <div className="font-OpenSansRegular font-[700] text-[13px] flex items-center justify-center h-[40px] text-titleColor text-center">
                PO #
              </div>
            </th>
            <th className="border-r-[1px] border-borderColor">
              <div className="font-OpenSansRegular font-[700] text-[13px] flex items-center pl-4 h-[40px] text-titleColor">
                Description
              </div>
            </th>
            <th className="border-r-[1px] border-borderColor w-[150px]">
              <div className="font-OpenSansRegular font-[700] text-[13px] flex items-center justify-center h-[40px] text-titleColor text-center">
                Name
              </div>
            </th>
            {isSuperAdmin && (
              <th className="border-r-[1px] border-borderColor w-[130px]">
                <div className="font-OpenSansBold text-[13px] flex items-center justify-center h-[40px] text-titleColor text-center">
                  Company
                </div>
              </th>
            )}
            <th className="border-r-[1px] border-borderColor w-[150px]">
              <div className="font-OpenSansRegular font-[700] text-[13px] flex items-center justify-center h-[40px] text-titleColor text-center">
                Date opened
              </div>
            </th>
            <th className="w-[110px]">
              <div className="font-OpenSansRegular font-[700] text-[13px] flex items-center justify-center h-[40px] text-titleColor text-center">
                Status
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {tickets.map((ticket, index) => (
            <tr
              key={ticket.id || ticket._id || ticket.ticketId || ticket.ticketNumber || ticket.number || index}
              onClick={() => {
                const ticketId =
                  ticket.id || ticket._id || ticket.ticketId || ticket.ticketNumber || ticket.number || index;
                const displayNumber = ticket.ticketNumber ?? ticket.number ?? ticket.ticketNo ?? ticketId;
                dispatch(setTicketNumber(String(displayNumber)));
                navigate(`/ticket/detail/${ticketId}`, { state: { ticket } });
              }}
              className="border-b-[1px] border-borderColor h-[40px] cursor-pointer"
            >
              <td className="border-r-[1px] border-borderColor">
                <div className="font-OpenSansRegular px-[5px] py-[5px] text-center text-[12px] font-[400] text-titleColor flex items-center justify-center gap-1 ">
                  {unreadIds.has(ticket.id) && (
                    <span className="w-[8px] h-[8px] rounded-full bg-red-500 shrink-0 " />
                  )}
                  {ticket.ticketNumber ?? ticket.number ?? ticket.ticketNo ?? ""}
                </div>
              </td>
              <td className="border-r-[1px] border-borderColor">
                <div className="font-OpenSansRegular px-[5px] py-[5px] text-center text-[12px] font-[400] text-titleColor">
                  {ticket.woNumber ?? ticket.workOrderNumber ?? ticket.wo?.number ?? ""}
                </div>
              </td>
              <td className="border-r-[1px] border-borderColor">
                <div className="font-OpenSansRegular px-[5px] py-[5px] text-center text-[12px] font-[400] text-titleColor">
                  {ticket.poNumber ?? ticket.purchaseOrderNumber ?? ticket.po?.number ?? ""}
                </div>
              </td>
              <td className="border-r-[1px] border-borderColor">
                <div className="font-OpenSansRegular px-[10px] py-[5px] text-[12px] font-[400] text-titleColor">
                  {ticket.description ?? ticket.issue ?? ticket.title ?? ""}
                </div>
              </td>
              <td className="border-r-[1px] border-borderColor">
                <div className="font-OpenSansRegular px-[5px] py-[5px] text-center text-[12px] font-[400] text-titleColor">
                  {ticket.createdByUser?.name ?? ticket.createdByCustomer?.name ?? ""}
                </div>
              </td>
              {isSuperAdmin && (
                <td className="border-r-[1px] border-borderColor">
                  <div className="font-OpenSansRegular px-[5px] py-[5px] text-center text-[12px] text-titleColor">
                    {companyNameByIdentifier[ticket.company] ?? ticket.company ?? ""}
                  </div>
                </td>
              )}
              <td className="border-r-[1px] border-borderColor">
                <div className="font-OpenSansRegular px-[5px] py-[5px] text-center text-[12px] font-[400] text-titleColor">
                  {ticket.createdAt
                    ? formatDate(ticket.createdAt)
                    : ticket.dateOpened ?? ""}
                </div>
              </td>
              <td>
                <div
                  className={`mx-auto w-[72px] h-[22px] flex items-center justify-center rounded-[11px] font-OpenSansRegular font-[400] text-titleColor text-[11px] ${getStatusLabel(ticket) === "Open" ? "bg-wo_color2" : "bg-wo_color6"
                    }`}
                >
                  {getStatusLabel(ticket)}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default TicketList;